import { useCallback } from "react";
import { ArrowDown, ArrowUp, BarChart2, ChevronDown, LayoutGrid, List, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@nexus/ui";
import { useDashboardFiltersStore } from "@/features/dashboard/stores/dashboardFilters.store";
import {
  useDrilldownFiltersStore,
  SORT_MODE_OPTIONS,
  type DrilldownViewMode,
  type DrilldownSortMode,
} from "../stores/drilldownFilters.store";
import { useKpiSelectorCatalog } from "@/features/kpi-settings/hooks/useKpiSelectorCatalog";
import { useCreativeUrlSettings } from "@/lib/queries/system-settings.queries";
import { downloadText } from "../lib/download";
import {
  buildDrilldownExportCsv,
  DRILLDOWN_CSV_MIME,
  DRILLDOWN_EXPORT_FILENAME,
} from "../lib/export-drilldown-csv";
import { MyBrandColumnsMenu } from "./MyBrandColumnsMenu";
import { MyBrandKpiPillRow } from "./MyBrandKpiPillRow";

const VIEW_MODES = [
  { value: "grid", labelKey: "drilldown.viewToggle.grid", defaultLabel: "Grid", Icon: LayoutGrid },
  { value: "list", labelKey: "drilldown.viewToggle.list", defaultLabel: "List", Icon: List },
  { value: "chart", labelKey: "drilldown.viewToggle.chart", defaultLabel: "Chart", Icon: BarChart2 },
] as const;

interface DrilldownViewModeToggleProps {
  rows: Array<Record<string, unknown>>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

export function DrilldownViewModeToggle({ rows, hasNextPage, isFetchingNextPage }: DrilldownViewModeToggleProps) {
  const { t } = useTranslation();
  const { competitorsPath } = useCreativeUrlSettings();
  const mode = useDashboardFiltersStore((s) => s.mode);
  const { myBrandCatalog, myBrandById, isLoading } = useKpiSelectorCatalog("drilldown");
  const viewMode = useDrilldownFiltersStore((s) => s.viewMode);
  const setViewMode = useDrilldownFiltersStore((s) => s.setViewMode);
  const competitorSortMode = useDrilldownFiltersStore((s) => s.competitorSortMode);
  const competitorSortDirection = useDrilldownFiltersStore((s) => s.competitorSortDirection);
  const myBrandSortMode = useDrilldownFiltersStore((s) => s.myBrandSortMode);
  const myBrandSortDirection = useDrilldownFiltersStore((s) => s.myBrandSortDirection);
  const appliedSegment1 = useDrilldownFiltersStore((s) => s.appliedSegment1);
  const appliedSegment2 = useDrilldownFiltersStore((s) => s.appliedSegment2);
  const appliedSegment3 = useDrilldownFiltersStore((s) => s.appliedSegment3);
  const appliedMyBrandSegment1 = useDrilldownFiltersStore((s) => s.appliedMyBrandSegment1);
  const appliedMyBrandSegment2 = useDrilldownFiltersStore((s) => s.appliedMyBrandSegment2);
  const appliedMyBrandSegment3 = useDrilldownFiltersStore((s) => s.appliedMyBrandSegment3);

  const segment1 = mode === "my-brand" ? appliedMyBrandSegment1 : appliedSegment1;
  const segment2 = mode === "my-brand" ? appliedMyBrandSegment2 : appliedSegment2;
  const segment3 = mode === "my-brand" ? appliedMyBrandSegment3 : appliedSegment3;
  const rowCount = rows.length;

  const handleDownload = useCallback(() => {
    downloadText(
      buildDrilldownExportCsv({
        rows,
        mode: mode === "my-brand" ? "my-brand" : "competitors",
        segments: [segment1, segment2, segment3],
        kpis: myBrandCatalog.map((kpi) => ({ id: kpi.id, label: kpi.label })),
        competitorsPath,
      }),
      DRILLDOWN_EXPORT_FILENAME,
      DRILLDOWN_CSV_MIME,
    );
  }, [rows, mode, segment1, segment2, segment3, myBrandCatalog, competitorsPath]);

  const currentSortLabel =
    mode === "my-brand"
      ? myBrandById[myBrandSortMode]?.label ?? myBrandSortMode
      : SORT_MODE_OPTIONS.find((o) => o.value === competitorSortMode)?.label ?? competitorSortMode;
  const currentSortDirection = mode === "my-brand" ? myBrandSortDirection : competitorSortDirection;

  return (
    <div className="flex flex-col gap-4">
      {/* Row 1: view mode toggle + sort controls */}
      <div className="flex items-center justify-between gap-4">
        <div
          role="group"
          aria-label={t("drilldown.viewToggle.viewModeGroup", "View mode")}
          className="inline-flex w-fit items-center gap-0.5 rounded-[10px] border border-border bg-background p-[3px]"
        >
          {VIEW_MODES.map(({ value, labelKey, defaultLabel, Icon }) => {
            const active = viewMode === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={active}
                onClick={() => setViewMode(value as DrilldownViewMode)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {t(labelKey, defaultLabel)}
              </button>
            );
          })}
        </div>

        <div className="flex min-w-0 flex-1 items-start justify-end gap-2">
          {/* Sort controls: competitors keeps the fixed Order-by dropdown; My Brand's
              KPI pill row both picks visible columns and drives the sort (see spec). */}
          {mode === "my-brand" ? <MyBrandKpiPillRow /> : <CompetitorSortControls />}
          {mode === "my-brand" && viewMode === "list" ? <MyBrandColumnsMenu /> : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            iconStart="download"
            disabled={rowCount === 0 || (mode === "my-brand" && isLoading)}
            onClick={handleDownload}
            data-testid="drilldown-export"
            aria-label={t("drilldown.viewToggle.downloadAria", "Export creative links as CSV")}
            className="shrink-0"
          >
            {t("drilldown.viewToggle.download", "Export")}
          </Button>
        </div>
      </div>

      {/* Row 2: result count */}
      <div className="flex items-center gap-2 whitespace-nowrap text-xs text-muted-foreground">
        {isFetchingNextPage && <Loader2 className="h-3 w-3 animate-spin" />}
        {rowCount > 0 && (
          <span>
            {hasNextPage
              ? t(
                  "drilldown.viewToggle.resultsSummaryWithMore",
                  "Showing {{rowCount}} results · Sorted by {{label}} ({{direction}}) · Scroll to load more",
                  { rowCount: rowCount.toLocaleString(), label: currentSortLabel, direction: currentSortDirection },
                )
              : t(
                  "drilldown.viewToggle.resultsSummary",
                  "Showing {{rowCount}} results · Sorted by {{label}} ({{direction}})",
                  { rowCount: rowCount.toLocaleString(), label: currentSortLabel, direction: currentSortDirection },
                )}
          </span>
        )}
      </div>
    </div>
  );
}

/** Competitors-only "Order by" dropdown + Asc/Desc toggle — unchanged behavior, just
 *  reading the renamed `competitorSortMode`/`competitorSortDirection` store fields. */
function CompetitorSortControls() {
  const { t } = useTranslation();
  const sortMode = useDrilldownFiltersStore((s) => s.competitorSortMode);
  const sortDirection = useDrilldownFiltersStore((s) => s.competitorSortDirection);
  const setSortMode = useDrilldownFiltersStore((s) => s.setCompetitorSortMode);
  const setSortDirection = useDrilldownFiltersStore((s) => s.setCompetitorSortDirection);
  const currentSortLabel = SORT_MODE_OPTIONS.find((o) => o.value === sortMode)?.label ?? sortMode;

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-[10px] border border-border bg-background px-3 py-[7px] text-xs font-medium transition-colors hover:bg-muted/40"
          >
            <span className="text-muted-foreground">{t("drilldown.viewToggle.orderBy", "Order by")}</span>
            <span className="text-foreground">{currentSortLabel}</span>
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {SORT_MODE_OPTIONS.map(({ label, value }) => (
            <DropdownMenuItem
              key={value}
              onSelect={() => setSortMode(value as DrilldownSortMode)}
              className={sortMode === value ? "font-medium text-primary" : ""}
            >
              {label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <div
        role="group"
        aria-label={t("drilldown.viewToggle.sortDirectionGroup", "Sort direction")}
        className="inline-flex items-center overflow-hidden rounded-[10px] border border-border bg-background"
      >
        <button
          type="button"
          aria-pressed={sortDirection === "asc"}
          onClick={() => setSortDirection("asc")}
          className={`inline-flex items-center gap-1.5 border-r border-border px-3 py-[7px] text-xs font-medium transition-colors ${
            sortDirection === "asc"
              ? "bg-foreground text-background"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("drilldown.viewToggle.asc", "Asc")}
          <ArrowUp className="h-3 w-3" />
        </button>
        <button
          type="button"
          aria-pressed={sortDirection === "desc"}
          onClick={() => setSortDirection("desc")}
          className={`inline-flex items-center gap-1.5 px-3 py-[7px] text-xs font-medium transition-colors ${
            sortDirection === "desc"
              ? "bg-foreground text-background"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("drilldown.viewToggle.desc", "Desc")}
          <ArrowDown className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
